import React, { useCallback, useState } from 'react';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';

// Shared building blocks for data tables (user and admin UIs).
//
// Composition:
//   <TableCard>
//     <TableHeader title="Jobs" search={…} pagination={…}>…filters…</TableHeader>
//     <TableViewport minWidthClass="min-w-[960px]">
//       <table className="w-full">…</table>
//     </TableViewport>
//   </TableCard>
//
// All controls live in the header — tables have no footer.
// Width: the viewport scrolls horizontally; the table declares its own
// min-width so columns never get crushed.
// Height: tables grow and the page scrolls.

// Shared cell styles. Cells never wrap: long free-text columns must opt into
// `max-w-* truncate` instead, and the viewport scrolls horizontally.
export const thClass =
    'text-left text-sm font-medium text-secondary uppercase tracking-wider px-4 py-3 whitespace-nowrap';
export const tdClass = 'px-4 py-3 text-sm text-body-color whitespace-nowrap';

export function TableCard({ children, className }: { children: React.ReactNode; className?: string }) {
    return (
        <div className={`bg-widget-bg rounded-lg border border-gray-700 flex flex-col overflow-hidden ${className ?? ''}`}>
            {children}
        </div>
    );
}

export function TableViewport({
    children,
    className,
}: {
    children: React.ReactNode;
    className?: string;
}) {
    return (
        <div className={`overflow-x-auto ${className ?? ''}`}>
            {children}
        </div>
    );
}

export const PAGE_SIZE_OPTIONS = [20, 50, 100];
export const DEFAULT_PAGE_SIZE = 20;

// Per-table page size persisted across sessions.
export function usePageSize(tableKey: string, defaultSize: number = DEFAULT_PAGE_SIZE): [number, (size: number) => void] {
    const storageKey = `tablePageSize:${tableKey}`;
    const [size, setSize] = useState<number>(() => {
        try {
            const stored = Number(localStorage.getItem(storageKey));
            return PAGE_SIZE_OPTIONS.includes(stored) ? stored : defaultSize;
        } catch {
            return defaultSize;
        }
    });
    const update = useCallback(
        (next: number) => {
            setSize(next);
            try {
                localStorage.setItem(storageKey, String(next));
            } catch {
                // Storage may be unavailable (private mode); the in-memory value still applies.
            }
        },
        [storageKey],
    );
    return [size, update];
}

export interface TablePaginationProps {
    offset: number;
    limit: number;
    total: number;
    onOffsetChange: (offset: number) => void;
    // When provided, renders a page-size select. The caller must reset its
    // offset to 0 (TablePagination calls onOffsetChange(0) before onLimitChange).
    onLimitChange?: (limit: number) => void;
    pageSizeOptions?: number[];
}

export interface TableSearchProps {
    value: string;
    onChange: (value: string) => void;
    // Keep the placeholder short ("Search..."); search covers all columns,
    // so no field list is needed.
    placeholder?: string;
    ariaLabel?: string;
}

const headerButtonClass =
    'px-3 py-1.5 bg-body-bg border border-gray-600 text-body-color rounded-md text-sm cursor-pointer ' +
    'hover:bg-body-bg/50 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed';
const headerSelectClass =
    'bg-body-bg border border-gray-600 text-body-color focus:border-primary focus:outline-none rounded-md pl-2 pr-7 py-1.5 text-sm select-chevron';

// Compact count + page size + Previous/Next cluster for table headers.
export function TablePagination({
    offset,
    limit,
    total,
    onOffsetChange,
    onLimitChange,
    pageSizeOptions = PAGE_SIZE_OPTIONS,
}: TablePaginationProps) {
    const showStart = total === 0 ? 0 : offset + 1;
    const showEnd = Math.min(offset + limit, total);

    return (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <span className="text-sm text-secondary whitespace-nowrap">
                {total > 0 ? `${showStart}-${showEnd} of ${total}` : 'No results'}
            </span>
            {onLimitChange && (
                <select
                    aria-label="Rows per page"
                    title="Rows per page"
                    className={headerSelectClass}
                    value={limit}
                    onChange={(e) => {
                        onOffsetChange(0);
                        onLimitChange(Number(e.target.value));
                    }}
                >
                    {pageSizeOptions.map((size) => (
                        <option key={size} value={size}>
                            {size} / page
                        </option>
                    ))}
                </select>
            )}
            <div className="flex space-x-2">
                <button
                    className={headerButtonClass}
                    disabled={offset === 0}
                    onClick={() => onOffsetChange(Math.max(0, offset - limit))}
                >
                    Prev
                </button>
                <button
                    className={headerButtonClass}
                    disabled={offset + limit >= total}
                    onClick={() => onOffsetChange(offset + limit)}
                >
                    Next
                </button>
            </div>
        </div>
    );
}

// Standard table search box: magnifier icon, clear button, header-control
// sizing (py-1.5 text-sm) so it lines up with pagination and filters.
export function TableSearchInput({ value, onChange, placeholder, ariaLabel }: TableSearchProps) {
    return (
        <div className="relative min-w-[200px]">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary pointer-events-none" />
            <input
                aria-label={ariaLabel ?? 'Search'}
                type="text"
                placeholder={placeholder ?? 'Search...'}
                className="w-full bg-body-bg border border-gray-600 text-body-color focus:border-primary focus:outline-none rounded-md pl-9 pr-8 py-1.5 text-sm"
                value={value}
                onChange={(e) => onChange(e.target.value)}
            />
            {value && (
                <button
                    aria-label="Clear search"
                    onClick={() => onChange('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-secondary hover:text-body-color"
                >
                    <XMarkIcon className="w-4 h-4" />
                </button>
            )}
        </div>
    );
}

export type SortOrder = 'asc' | 'desc';

// Comparator for client-side sorting of fully loaded tables: nulls last,
// numbers numerically, strings case-insensitively.
export function compareRowValues(a: unknown, b: unknown): number {
    if (a == null && b == null) return 0;
    if (a == null) return 1;
    if (b == null) return -1;
    if (typeof a === 'number' && typeof b === 'number') return a - b;
    if (typeof a === 'boolean' && typeof b === 'boolean') return Number(a) - Number(b);
    return String(a).localeCompare(String(b), undefined, { sensitivity: 'base' });
}

// Sortable column header; click toggles asc/desc for the given key.
// firstOrder is the direction used when the column becomes active
// (asc for text columns, desc for time/numeric ones).
export function SortableTh({
    label,
    sortKey,
    sort,
    order,
    onSort,
    className,
    firstOrder = 'desc',
}: {
    label: string;
    sortKey: string;
    sort: string;
    order: SortOrder;
    onSort: (sort: string, order: SortOrder) => void;
    className?: string;
    firstOrder?: SortOrder;
}) {
    const isActive = sort === sortKey;
    const nextOrder: SortOrder = isActive ? (order === 'desc' ? 'asc' : 'desc') : firstOrder;
    return (
        <th
            className={`${thClass} cursor-pointer select-none hover:text-body-color ${className ?? ''}`}
            onClick={() => onSort(sortKey, nextOrder)}
        >
            {label}
            <span className="ml-1">{isActive ? (order === 'asc' ? '▲' : '▼') : '↕'}</span>
        </th>
    );
}

export function EmptyRow({ colSpan, text }: { colSpan: number; text: string }) {
    return (
        <tr>
            <td colSpan={colSpan} className="px-4 py-3 text-sm text-secondary text-center">
                {text}
            </td>
        </tr>
    );
}

// Single header bar for every table: title, search, and table-local controls
// (children) on the left, pagination on the right. Wraps on narrow screens.
export function TableHeader({
    title,
    search,
    pagination,
    children,
    className,
}: {
    title?: React.ReactNode;
    search?: TableSearchProps;
    pagination?: TablePaginationProps;
    children?: React.ReactNode;
    className?: string;
}) {
    return (
        <div className={`flex flex-wrap items-center gap-x-4 gap-y-3 px-4 py-3 border-b border-gray-700 ${className ?? ''}`}>
            {title != null && <h3 className="text-base font-semibold text-heading whitespace-nowrap">{title}</h3>}
            {search && <TableSearchInput {...search} />}
            {children}
            {pagination && (
                <div className="ml-auto">
                    <TablePagination {...pagination} />
                </div>
            )}
        </div>
    );
}

// Page/section-level filter bar. Holds dataset-scoping filters (instance,
// status, dates, ...) that drive every widget in the group below it — never
// table-only controls like search or pagination. Pages with unrelated groups
// use one bar per group, right above that group.
export function SectionFilterBar({ children, className }: { children: React.ReactNode; className?: string }) {
    return <div className={`mb-4 flex flex-wrap items-center gap-2 ${className ?? ''}`}>{children}</div>;
}
